import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faPlus, faChevronDown, faChevronUp, faClock, faSignal, faTag, faRocket, faPlay, faShieldAlt, faPencilAlt, faTrash, faGlobe, faExchangeAlt, faCheck, faTimes, faBolt, faChartBar, faClipboardCheck } from '@fortawesome/free-solid-svg-icons';
import actionService from '../../services/actionService';
import campaignService from '../../services/campaignService';
import riskScoringService from '../../services/riskScoringService';
import crossFrameworkService from '../../services/crossFrameworkService';
import owaspWstgService from '../../services/owaspWstgService';
import ActionDetailModal from '../common/ActionDetailModal';
import MitrePlaybookBuilder from '../common/MitrePlaybookBuilder';
import WstgPlaybookBuilder from '../common/WstgPlaybookBuilder';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../config/axiosConfig';
import PageHeader from '../common/PageHeader';

const CampaignView = ({ projectId: propProjectId, mode: propMode }) => {
  const { id, projectId: routeProjectId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const { user } = useAuth();
  const projectId = propProjectId || routeProjectId || id;
  const mode = propMode || (user?.role === 'blue_team' ? 'blue' : 'red');
  const [actions, setActions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showWstgBuilder, setShowWstgBuilder] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState('mitre_attack');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState(null);
  const [crossMatrix, setCrossMatrix] = useState(null);
  const [showCrossMatrix, setShowCrossMatrix] = useState(false);

  // Campaign Templates
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState({});
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Coverage dashboard
  const [coverageData, setCoverageData] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [generatingPlaybook, setGeneratingPlaybook] = useState(false);

  // For creating/editing campaigns (legacy modal)
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    objective: '',
    actions: []
  });

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    try {
      if (!projectId) {
        toast.error('Project ID is missing');
        setLoading(false);
        return;
      }

      const actionsData = await actionService.getActionsByProject(projectId);
      setActions(actionsData);

      try {
        const campaignsData = await campaignService.getCampaignsByProject(projectId);
        // Blue team only sees campaigns they created
        if (mode === 'blue') {
          setCampaigns(campaignsData.filter(c => c.createdBy === 'blue_team'));
        } else {
          setCampaigns(campaignsData);
        }
      } catch (error) {
        setCampaigns([]);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load campaign data');
      setLoading(false);
    }
  };

  // Fetch campaign templates
  const fetchTemplates = async () => {
    if (Object.keys(templates).length > 0) return;
    setTemplatesLoading(true);
    try {
      const response = await axios.get('/campaign-templates/grouped');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load campaign templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(selectedTemplate?._id === template._id ? null : template);
  };

  const handleCreateFromTemplate = async (template) => {
    try {
      const response = await axios.post(`/campaign-templates/${template._id}/create-campaign`, {
        projectId,
        customName: template.name,
        customObjective: template.objective
      });
      setCampaigns([...campaigns, response.data.campaign]);
      setSelectedCampaign(response.data.campaign);
      setShowTemplates(false);
      setSelectedTemplate(null);
      toast.success(`Campaign "${response.data.campaign.name}" created from template!`);
      if (response.data.suggestedTechniques?.length > 0) {
        toast.info(`This campaign suggests ${response.data.suggestedTechniques.length} MITRE ATT&CK techniques to test.`);
      }
    } catch (error) {
      console.error('Error creating from template:', error);
      toast.error('Failed to create campaign from template');
    }
  };

  const handleCreatePlaybookFromTemplate = async (template) => {
    try {
      const response = await axios.post(`/campaign-templates/${template._id}/create-playbook`, {
        projectId,
        customName: `${template.name} Playbook`
      });
      setShowTemplates(false);
      setSelectedTemplate(null);
      toast.success(response.data.message);
      if (response.data.atomicTestsFound === 0) {
        toast.warning('No Atomic Red Team tests found for the suggested techniques.');
      } else {
        toast.info(`Playbook includes ${response.data.atomicTestsFound} Atomic Red Team tests.`);
      }
    } catch (error) {
      console.error('Error creating playbook from template:', error);
      toast.error(error.response?.data?.message || 'Failed to create playbook from template');
    }
  };

  const categoryNames = {
    'initial-access': 'Initial Access',
    'privilege-escalation': 'Privilege Escalation',
    'lateral-movement': 'Lateral Movement',
    'persistence': 'Persistence',
    'exfiltration': 'Data Exfiltration',
    'defense-evasion': 'Defense Evasion',
    'full-chain': 'Full Kill Chain',
    'specialized': 'Specialized'
  };

  const difficultyColors = {
    'beginner': 'bg-green-100 text-green-800',
    'intermediate': 'bg-yellow-100 text-yellow-800',
    'advanced': 'bg-orange-100 text-orange-800',
    'expert': 'bg-red-100 text-red-800'
  };

  // Get actions for a campaign
  const getCampaignActions = (campaign) => {
    if (!campaign) return [];
    let actionIds = [];
    if (campaign.actions && Array.isArray(campaign.actions)) {
      actionIds = campaign.actions.map(a => {
        if (typeof a === 'string') return a;
        if (a && a._id) return a._id;
        return null;
      }).filter(id => id !== null);
    } else if (campaign.actionIds && Array.isArray(campaign.actionIds)) {
      actionIds = campaign.actionIds;
    }
    return actions.filter(a => actionIds.includes(a._id));
  };

  // Get attack chain visualization
  const getAttackChain = (campaign) => {
    const chain = [];
    const tactics = [
      'Reconnaissance', 'Resource Development', 'Initial Access', 'Execution',
      'Persistence', 'Privilege Escalation', 'Defense Evasion', 'Credential Access',
      'Discovery', 'Lateral Movement', 'Collection', 'Command and Control',
      'Exfiltration', 'Impact'
    ];

    const campaignActions = getCampaignActions(campaign);
    const tacticGroups = {};
    campaignActions.forEach(action => {
      if (action.mitreAttackTechnique?.tactic) {
        const tactic = action.mitreAttackTechnique.tactic;
        if (!tacticGroups[tactic]) tacticGroups[tactic] = [];
        tacticGroups[tactic].push(action);
      }
    });

    tactics.forEach(tactic => {
      if (tacticGroups[tactic]) {
        chain.push({
          tactic,
          actions: tacticGroups[tactic],
          success: tacticGroups[tactic].some(a => a.result === 'successful')
        });
      }
    });

    return chain;
  };

  const handleCreateCampaign = () => {
    setEditingCampaign(null);
    if (selectedFramework === 'owasp_wstg') {
      setShowWstgBuilder(true);
    } else if (selectedFramework === 'custom') {
      setShowCreateModal(true);
      setCampaignForm({ name: '', description: '', objective: '', actions: [] });
    } else {
      setShowBuilder(true);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    if (campaign.framework === 'owasp_wstg') {
      setShowWstgBuilder(true);
    } else {
      setShowBuilder(true);
    }
  };

  // Handle save from WstgPlaybookBuilder
  const handleWstgBuilderSave = async (data) => {
    try {
      if (editingCampaign) {
        const updatedCampaign = {
          name: data.name,
          description: data.description,
          objective: data.objective,
          project: projectId,
          framework: 'owasp_wstg',
          wstgTests: data.wstgTests.map(t => ({
            testId: t.testId, categoryCode: t.categoryCode, name: t.name || t.testName,
            severity: t.severity, status: 'pending',
            mitreLinks: t.mitreMapping || []
          })),
          webTarget: data.webTargetId || null,
          status: editingCampaign.status || 'planned',
          createdBy: mode === 'blue' ? 'blue_team' : 'red_team'
        };
        const result = await campaignService.updateCampaign(editingCampaign._id || editingCampaign.id, updatedCampaign);
        setCampaigns(campaigns.map(c =>
          (c._id === editingCampaign._id || c.id === editingCampaign.id) ? result : c
        ));
        setSelectedCampaign(result);
        toast.success('WSTG campaign updated successfully');
      } else {
        const newCampaign = {
          name: data.name,
          description: data.description,
          objective: data.objective,
          project: projectId,
          framework: 'owasp_wstg',
          wstgTests: data.wstgTests.map(t => ({
            testId: t.testId, categoryCode: t.categoryCode, name: t.name || t.testName,
            severity: t.severity, status: 'pending',
            mitreLinks: t.mitreMapping || []
          })),
          webTarget: data.webTargetId || null,
          actions: [],
          status: 'planned',
          createdBy: mode === 'blue' ? 'blue_team' : 'red_team'
        };
        const result = await campaignService.createCampaign(newCampaign);
        setCampaigns([...campaigns, result]);
        setSelectedCampaign(result);
        toast.success('WSTG campaign created successfully');
      }
      setShowWstgBuilder(false);
      setEditingCampaign(null);
    } catch (error) {
      console.error('Error saving WSTG campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to save WSTG campaign');
    }
  };

  // Load coverage when a campaign with techniques is selected
  useEffect(() => {
    if (selectedCampaign && selectedCampaign.techniques?.length > 0 && selectedCampaign.framework !== 'owasp_wstg') {
      fetchCoverage(selectedCampaign._id || selectedCampaign.id);
    } else {
      setCoverageData(null);
    }
  }, [selectedCampaign?._id, selectedCampaign?.id]);

  const fetchCoverage = async (campaignId) => {
    setCoverageLoading(true);
    try {
      const data = await campaignService.getCoverage(campaignId);
      setCoverageData(data);
    } catch (error) {
      setCoverageData(null);
    } finally {
      setCoverageLoading(false);
    }
  };

  const handleGeneratePlaybook = async (untestedOnly = false) => {
    const campaignId = selectedCampaign._id || selectedCampaign.id;
    setGeneratingPlaybook(true);
    try {
      const result = await campaignService.generatePlaybook(campaignId, { untestedOnly });
      toast.success(`Playbook created with ${result.playbook.steps.length} steps`);
      if (result.unmatchedTechniques.length > 0) {
        toast.warning(`${result.unmatchedTechniques.length} techniques had no atomic tests: ${result.unmatchedTechniques.map(t => t.id).join(', ')}`);
      }
      navigate(`/playbooks/${result.playbook._id}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.warning(error.response?.data?.message || 'No atomic tests found for campaign techniques');
      } else {
        toast.error(error.response?.data?.message || 'Failed to generate playbook');
      }
    } finally {
      setGeneratingPlaybook(false);
    }
  };

  const handleCreateActionsFromExecution = async () => {
    const campaignId = selectedCampaign._id || selectedCampaign.id;
    if (!selectedCampaign.playbookExecution) {
      toast.warning('No playbook execution linked to this campaign');
      return;
    }
    try {
      const response = await axios.post(`/playbooks/executions/${selectedCampaign.playbookExecution}/create-actions`);
      toast.success(`${response.data.createdActions} actions created from execution results`);
      fetchData();
      fetchCoverage(campaignId);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create actions from execution');
    }
  };

  // Load cross-framework matrix for selected campaign
  const loadCrossMatrix = async (campaignId) => {
    try {
      const matrix = await crossFrameworkService.getMatrix(campaignId);
      setCrossMatrix(matrix);
      setShowCrossMatrix(true);
    } catch (error) {
      console.error('Failed to load cross-framework matrix:', error);
      toast.error('Failed to load cross-framework data');
    }
  };

  // Handle save from MitrePlaybookBuilder
  const handleBuilderSave = async (data) => {
    try {
      if (editingCampaign) {
        const updatedCampaign = {
          name: data.name,
          description: data.description,
          objective: data.objective,
          project: projectId,
          techniques: data.techniques,
          actions: editingCampaign.actions?.map(a => a._id || a) || [],
          status: editingCampaign.status || 'planned',
          createdBy: mode === 'blue' ? 'blue_team' : 'red_team'
        };
        const result = await campaignService.updateCampaign(editingCampaign._id || editingCampaign.id, updatedCampaign);
        setCampaigns(campaigns.map(c =>
          (c._id === editingCampaign._id || c.id === editingCampaign.id) ? result : c
        ));
        setSelectedCampaign(result);
        toast.success('Campaign updated successfully');
      } else {
        const newCampaign = {
          name: data.name,
          description: data.description,
          objective: data.objective,
          project: projectId,
          techniques: data.techniques,
          actions: [],
          status: 'planned',
          createdBy: mode === 'blue' ? 'blue_team' : 'red_team'
        };
        const result = await campaignService.createCampaign(newCampaign);
        setCampaigns([...campaigns, result]);
        setSelectedCampaign(result);
        toast.success('Campaign created successfully');
      }
      setShowBuilder(false);
      setEditingCampaign(null);
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to save campaign');
    }
  };

  // Legacy modal save (for action-based campaigns)
  const handleSaveCampaign = async () => {
    if (!campaignForm.name || !campaignForm.objective || campaignForm.actions.length === 0) {
      toast.error('Please fill in all required fields and select at least one action');
      return;
    }
    try {
      if (editingCampaign) {
        const updatedCampaign = {
          name: campaignForm.name,
          description: campaignForm.description,
          objective: campaignForm.objective,
          project: projectId,
          actions: campaignForm.actions.map(a => a._id),
          startTime: campaignForm.actions[0]?.startTimestamp,
          endTime: campaignForm.actions[campaignForm.actions.length - 1]?.endTimestamp || campaignForm.actions[0]?.startTimestamp,
          isAutoDetected: false
        };
        const result = await campaignService.updateCampaign(editingCampaign._id || editingCampaign.id, updatedCampaign);
        setCampaigns(campaigns.map(c =>
          (c._id === editingCampaign._id || c.id === editingCampaign.id) ? result : c
        ));
        setSelectedCampaign(result);
        toast.success('Campaign updated successfully');
      } else {
        const newCampaign = {
          name: campaignForm.name,
          description: campaignForm.description,
          objective: campaignForm.objective,
          project: projectId,
          actions: campaignForm.actions.map(a => a._id),
          startTime: campaignForm.actions[0]?.startTimestamp,
          endTime: campaignForm.actions[campaignForm.actions.length - 1]?.endTimestamp || campaignForm.actions[0]?.startTimestamp,
          isAutoDetected: false
        };
        const result = await campaignService.createCampaign(newCampaign);
        setCampaigns([...campaigns, result]);
        setSelectedCampaign(result);
        toast.success('Campaign created successfully');
      }
      setShowCreateModal(false);
      setEditingCampaign(null);
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to save campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await campaignService.deleteCampaign(campaignId);
        const updatedCampaigns = campaigns.filter(c => c._id !== campaignId && c.id !== campaignId);
        setCampaigns(updatedCampaigns);
        if (selectedCampaign?._id === campaignId || selectedCampaign?.id === campaignId) {
          setSelectedCampaign(null);
        }
        toast.success('Campaign deleted successfully');
      } catch (error) {
        console.error('Error deleting campaign:', error);
        toast.error('Failed to delete campaign');
      }
    }
  };

  if (!projectId) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800">No project selected. Please select a project to view campaigns.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Full-screen MITRE builder overlay
  if (showBuilder) {
    return (
      <div className={`fixed inset-0 z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <MitrePlaybookBuilder
          mode={mode}
          initialTechniques={editingCampaign?.techniques || []}
          initialName={editingCampaign?.name || ''}
          initialObjective={editingCampaign?.objective || ''}
          initialDescription={editingCampaign?.description || ''}
          onSave={handleBuilderSave}
          onCancel={() => { setShowBuilder(false); setEditingCampaign(null); }}
        />
      </div>
    );
  }

  // Full-screen WSTG builder overlay
  if (showWstgBuilder) {
    return (
      <div className={`fixed inset-0 z-50 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <WstgPlaybookBuilder
          mode={mode}
          projectId={projectId}
          initialTests={editingCampaign?.wstgTests?.map(t => ({
            ...t, testName: t.name, mitreMapping: t.mitreLinks || []
          })) || []}
          initialName={editingCampaign?.name || ''}
          initialObjective={editingCampaign?.objective || ''}
          initialDescription={editingCampaign?.description || ''}
          initialWebTargetId={editingCampaign?.webTarget || null}
          onSave={handleWstgBuilderSave}
          onCancel={() => { setShowWstgBuilder(false); setEditingCampaign(null); }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title={mode === 'blue' ? (t('campaigns.assessmentPlanning') || 'Assessment Planning') : (t('campaigns.title') || 'Campaign Analysis')}
        subtitle={mode === 'blue'
          ? (t('campaigns.assessmentPlanningDesc') || 'Plan assessments by selecting MITRE ATT&CK techniques for the red team to test')
          : (t('campaigns.subtitle') || 'View and analyze attack campaigns and kill chains')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Campaign List */}
        <div className="lg:col-span-1">
          <div className={`rounded-lg shadow-md p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : ''}`}>{t('campaigns.campaigns') || 'Campaigns'}</h3>
              <div className="flex space-x-2">
                {mode === 'red' && (
                  <button
                    onClick={() => {
                      setShowTemplates(true);
                      fetchTemplates();
                    }}
                    className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 flex items-center"
                    title="Browse campaign templates"
                  >
                    <FontAwesomeIcon icon={faBook} className="mr-1" />
                    Templates
                  </button>
                )}
                <button
                  onClick={handleCreateCampaign}
                  className={`text-sm text-white px-3 py-1 rounded flex items-center ${
                    selectedFramework === 'owasp_wstg' ? 'bg-orange-600 hover:bg-orange-700' :
                    selectedFramework === 'custom' ? 'bg-gray-600 hover:bg-gray-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                  title={`Create new ${selectedFramework === 'owasp_wstg' ? 'WSTG' : selectedFramework === 'custom' ? 'custom' : 'MITRE'} campaign`}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-1" />
                  Create
                </button>
              </div>
            </div>

            {/* Framework Selector */}
            <div className={`flex rounded-lg overflow-hidden border mb-4 ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
              {[
                { key: 'mitre_attack', label: 'MITRE ATT&CK', icon: faShieldAlt, color: 'red' },
                { key: 'owasp_wstg', label: 'OWASP WSTG', icon: faGlobe, color: 'orange' },
                { key: 'custom', label: 'Custom', icon: faPencilAlt, color: 'gray' }
              ].map(fw => (
                <button
                  key={fw.key}
                  onClick={() => setSelectedFramework(fw.key)}
                  className={`flex-1 text-xs py-1.5 px-2 flex items-center justify-center gap-1 transition-colors ${
                    selectedFramework === fw.key
                      ? fw.color === 'red' ? 'bg-red-600 text-white' :
                        fw.color === 'orange' ? 'bg-orange-600 text-white' :
                        'bg-gray-600 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FontAwesomeIcon icon={fw.icon} />
                  <span className="hidden sm:inline">{fw.label}</span>
                </button>
              ))}
            </div>

            {campaigns.length === 0 ? (
              <div className="text-center py-4">
                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No campaigns yet</p>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Click Create to build a campaign using the MITRE ATT&CK tree
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {campaigns.map(campaign => {
                  const campaignActions = getCampaignActions(campaign);
                  const techniqueCount = campaign.techniques?.length || 0;
                  const wstgCount = campaign.wstgTests?.length || 0;
                  const isWstg = campaign.framework === 'owasp_wstg';
                  const isCustom = campaign.framework === 'custom';
                  return (
                    <div
                      key={campaign._id || campaign.id}
                      onClick={() => setSelectedCampaign(campaign)}
                      style={{
                        borderLeftWidth: '4px',
                        borderLeftColor: isWstg ? '#f97316' :
                          isCustom ? '#6b7280' :
                          campaign.origin === 'template' ? '#a855f7' :
                          campaign.origin === 'auto_detected' ? '#9ca3af' :
                          campaign.createdBy === 'blue_team' ? '#3b82f6' : '#ef4444'
                      }}
                      className={`p-3 rounded cursor-pointer transition-colors ${
                        (selectedCampaign?._id === campaign._id || selectedCampaign?.id === campaign.id)
                          ? darkMode ? 'bg-blue-900/30' : 'bg-blue-50'
                          : darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className={`font-medium ${darkMode ? 'text-white' : ''}`}>{campaign.name}</h4>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {isWstg && wstgCount > 0 && `${wstgCount} WSTG tests`}
                            {!isWstg && techniqueCount > 0 && `${techniqueCount} techniques`}
                            {!isWstg && techniqueCount > 0 && campaignActions.length > 0 && ' • '}
                            {campaignActions.length > 0 && `${campaignActions.length} actions`}
                            {!isWstg && techniqueCount === 0 && campaignActions.length === 0 && wstgCount === 0 && 'No items'}
                            {campaign.startTime && ` • ${format(new Date(campaign.startTime), 'MMM d, HH:mm')}`}
                          </p>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            {/* Framework badge */}
                            {isWstg && (
                              <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                                WSTG
                              </span>
                            )}
                            {isCustom && (
                              <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                Custom
                              </span>
                            )}
                            {!isWstg && !isCustom && campaign.techniques?.length > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                MITRE
                              </span>
                            )}
                            {campaign.status && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                campaign.status === 'planned' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                campaign.status === 'active' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              }`}>
                                {campaign.status}
                              </span>
                            )}
                            {campaign.createdBy && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                campaign.createdBy === 'blue_team' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                              }`}>
                                {campaign.createdBy === 'blue_team' ? 'Blue' : 'Red'}
                              </span>
                            )}
                            {campaign.origin && campaign.origin !== 'manual' && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                campaign.origin === 'template' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {campaign.origin === 'template' ? (t('campaigns.originTemplate') || 'Template') : (t('campaigns.originAutoDetected') || 'Auto')}
                              </span>
                            )}
                            {campaignActions.some(a => a.result === 'successful') && (
                              <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                {campaignActions.filter(a => a.result === 'successful').length} successful
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-1 ml-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditCampaign(campaign); }}
                            className={`p-1.5 rounded transition-colors ${darkMode ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-600' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}
                            title={t('common.edit') || 'Edit'}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} className="text-sm" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteCampaign(campaign._id || campaign.id); }}
                            className={`p-1.5 rounded transition-colors ${darkMode ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600' : 'text-gray-500 hover:text-red-600 hover:bg-red-50'}`}
                            title={t('common.delete') || 'Delete'}
                          >
                            <FontAwesomeIcon icon={faTrash} className="text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Unassigned Actions (red team only) */}
          {mode === 'red' && actions.length > 0 && (
            <div className={`rounded-lg shadow-md p-4 mt-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`font-semibold mb-3 ${darkMode ? 'text-white' : ''}`}>{t('campaigns.unassignedActions') || 'Unassigned Actions'}</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(() => {
                  const assignedActionIds = new Set();
                  campaigns.forEach(campaign => {
                    getCampaignActions(campaign).forEach(action => {
                      assignedActionIds.add(action._id);
                    });
                  });
                  const unassigned = actions.filter(action => !assignedActionIds.has(action._id));
                  if (unassigned.length === 0) {
                    return <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>All actions are assigned to campaigns</p>;
                  }
                  return unassigned.map(action => (
                    <div
                      key={action._id}
                      className={`p-2 rounded text-sm cursor-pointer ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-blue-50'}`}
                      onClick={() => setSelectedAction(action)}
                    >
                      <div className={`font-medium ${darkMode ? 'text-white' : ''}`}>{action.title}</div>
                      <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                        {format(new Date(action.startTimestamp), 'MMM d, HH:mm')}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Campaign Details */}
        <div className="lg:col-span-2">
          {selectedCampaign ? (
            <div className={`rounded-lg shadow-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : ''}`}>{selectedCampaign.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {selectedCampaign.status && (
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        selectedCampaign.status === 'planned' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                        selectedCampaign.status === 'active' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {selectedCampaign.status}
                      </span>
                    )}
                    {selectedCampaign.origin && selectedCampaign.origin !== 'manual' && (
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        selectedCampaign.origin === 'template' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {selectedCampaign.origin === 'template' ? (t('campaigns.originTemplate') || 'Template') : (t('campaigns.originAutoDetected') || 'Auto')}
                      </span>
                    )}
                    {selectedCampaign.techniques?.some(t => t.isCustom) && (
                      <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                        {selectedCampaign.techniques.filter(t => t.isCustom).length} {t('campaigns.customSteps') || 'custom steps'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditCampaign(selectedCampaign)}
                    className={`px-3 py-1 rounded ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCampaign(selectedCampaign._id || selectedCampaign.id)}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Campaign Overview */}
              <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-2 gap-4">
                  {selectedCampaign.startTime && (
                    <div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Duration</p>
                      <p className={`font-medium ${darkMode ? 'text-white' : ''}`}>
                        {format(new Date(selectedCampaign.startTime), 'MMM d, HH:mm')} -
                        {selectedCampaign.endTime && format(new Date(selectedCampaign.endTime), ' HH:mm')}
                      </p>
                    </div>
                  )}
                  {getCampaignActions(selectedCampaign).length > 0 && (
                    <div>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Success Rate</p>
                      <p className={`font-medium ${darkMode ? 'text-white' : ''}`}>
                        {(() => {
                          const ca = getCampaignActions(selectedCampaign);
                          return ca.length > 0
                            ? Math.round((ca.filter(a => a.result === 'successful').length / ca.length) * 100)
                            : 0;
                        })()}%
                      </p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Objective</p>
                    <p className={`font-medium ${darkMode ? 'text-white' : ''}`}>{selectedCampaign.objective || 'No objective specified'}</p>
                  </div>
                  {selectedCampaign.description && (
                    <div className="col-span-2">
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Description</p>
                      <p className={`font-medium ${darkMode ? 'text-white' : ''}`}>{selectedCampaign.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Techniques List (for technique-based campaigns) */}
              {selectedCampaign.techniques?.length > 0 && (
                <div className="mb-6">
                  <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : ''}`}>
                    <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                    Planned Techniques ({selectedCampaign.techniques.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedCampaign.techniques.map((tech, index) => (
                      <div key={tech.id} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          <span className={`text-sm font-bold ${darkMode ? 'text-gray-400' : 'text-gray-500'} w-6 text-center`}>{index + 1}</span>
                          {tech.isCustom ? (
                            <span className={`font-mono text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                              {t('campaigns.customStep') || 'Custom'}
                            </span>
                          ) : (
                            <span className={`font-mono text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                              {tech.id}
                            </span>
                          )}
                          <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>{tech.name}</span>
                          {tech.tactic && (
                            <span className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                              {tech.tactic}
                            </span>
                          )}
                        </div>
                        {(tech.notes || tech.description) && (
                          <p className={`mt-1 ml-9 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {tech.notes || tech.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coverage Dashboard + Generate Playbook (for MITRE campaigns with techniques) */}
              {selectedCampaign.techniques?.length > 0 && selectedCampaign.framework !== 'owasp_wstg' && (
                <div className="mb-6">
                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      onClick={() => handleGeneratePlaybook(false)}
                      disabled={generatingPlaybook}
                      className="px-3 py-2 rounded text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faBolt} />
                      {generatingPlaybook ? 'Generating...' : (t('campaigns.generatePlaybook') || 'Generate Playbook')}
                    </button>
                    {coverageData && coverageData.coverage.untestedTechniques.length > 0 && (
                      <button
                        onClick={() => handleGeneratePlaybook(true)}
                        disabled={generatingPlaybook}
                        className={`px-3 py-2 rounded text-sm font-medium flex items-center gap-2 ${
                          darkMode ? 'bg-yellow-700 text-yellow-100 hover:bg-yellow-600' : 'bg-yellow-500 text-white hover:bg-yellow-600'
                        } disabled:opacity-50`}
                      >
                        <FontAwesomeIcon icon={faBolt} />
                        {t('campaigns.generatePlaybookUntested') || 'Playbook for Untested'}
                      </button>
                    )}
                    {selectedCampaign.playbookExecution && (
                      <button
                        onClick={handleCreateActionsFromExecution}
                        className={`px-3 py-2 rounded text-sm font-medium flex items-center gap-2 ${
                          darkMode ? 'bg-blue-700 text-blue-100 hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        <FontAwesomeIcon icon={faClipboardCheck} />
                        {t('campaigns.createActionsFromExecution') || 'Create Actions from Results'}
                      </button>
                    )}
                  </div>

                  {/* Coverage progress */}
                  {coverageLoading ? (
                    <div className="flex items-center gap-2 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading coverage...</span>
                    </div>
                  ) : coverageData && (
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <h4 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : ''}`}>
                        <FontAwesomeIcon icon={faChartBar} />
                        {t('campaigns.coverageDashboard') || 'Technique Coverage'}
                      </h4>

                      {/* Progress bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                            {coverageData.coverage.testedCount} / {coverageData.coverage.totalTechniques} {t('campaigns.techniquesTested') || 'techniques tested'}
                          </span>
                          <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>
                            {coverageData.coverage.totalTechniques > 0
                              ? Math.round((coverageData.coverage.testedCount / coverageData.coverage.totalTechniques) * 100)
                              : 0}%
                          </span>
                        </div>
                        <div className={`w-full rounded-full h-2.5 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                          <div
                            className="bg-green-500 h-2.5 rounded-full transition-all"
                            style={{ width: `${coverageData.coverage.totalTechniques > 0 ? (coverageData.coverage.testedCount / coverageData.coverage.totalTechniques) * 100 : 0}%` }}
                          />
                        </div>
                      </div>

                      {/* Per-technique grid */}
                      <div className="flex flex-wrap gap-1.5">
                        {coverageData.coverage.perTechnique.map(tech => (
                          <span
                            key={tech.id}
                            title={`${tech.id} - ${tech.name} (${tech.tactic || 'N/A'}) - ${tech.status}`}
                            className={`text-xs px-2 py-1 rounded font-mono cursor-default ${
                              tech.status === 'successful'
                                ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800')
                                : tech.status === 'failed'
                                ? (darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800')
                                : tech.status === 'tested'
                                ? (darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800')
                                : (darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-200 text-gray-500')
                            }`}
                          >
                            {tech.id}
                          </span>
                        ))}
                      </div>

                      {/* Legend */}
                      <div className={`flex gap-4 mt-3 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="flex items-center gap-1">
                          <span className={`inline-block w-3 h-3 rounded ${darkMode ? 'bg-green-900/40' : 'bg-green-100'}`} />
                          Successful
                        </span>
                        <span className="flex items-center gap-1">
                          <span className={`inline-block w-3 h-3 rounded ${darkMode ? 'bg-red-900/40' : 'bg-red-100'}`} />
                          Failed
                        </span>
                        <span className="flex items-center gap-1">
                          <span className={`inline-block w-3 h-3 rounded ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`} />
                          Untested
                        </span>
                      </div>

                      {/* Execution status */}
                      {coverageData.executionStatus && (
                        <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Execution: {coverageData.executionStatus.status} ({coverageData.executionStatus.progress.percentage}%)
                          </span>
                        </div>
                      )}

                      {coverageData.coverage.testedCount === coverageData.coverage.totalTechniques && coverageData.coverage.totalTechniques > 0 && (
                        <p className={`mt-2 text-sm font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          {t('campaigns.allTested') || 'All techniques tested'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* WSTG Tests (for WSTG campaigns) */}
              {selectedCampaign.framework === 'owasp_wstg' && selectedCampaign.wstgTests?.length > 0 && (
                <div className="mb-6">
                  <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : ''}`}>
                    <FontAwesomeIcon icon={faGlobe} className="mr-2 text-orange-500" />
                    WSTG Tests ({selectedCampaign.wstgTests.length})
                  </h4>
                  <div className="space-y-2">
                    {(() => {
                      const grouped = {};
                      selectedCampaign.wstgTests.forEach(test => {
                        if (!grouped[test.categoryCode]) grouped[test.categoryCode] = [];
                        grouped[test.categoryCode].push(test);
                      });
                      return Object.entries(grouped).map(([code, tests]) => {
                        const config = owaspWstgService.getCategoryConfig(`WSTG-${code}`);
                        return (
                          <div key={code} className={`rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} p-3`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>{config.name}</span>
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>({tests.length})</span>
                            </div>
                            <div className="space-y-1">
                              {tests.map(test => (
                                <div key={test.testId} className="flex items-center gap-2 text-sm">
                                  <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                                    darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                                  }`}>{test.testId}</span>
                                  <span className={`truncate flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{test.name}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    test.status === 'passed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                    test.status === 'failed' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                    test.status === 'not_applicable' ? (darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600') :
                                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                  }`}>{test.status}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* MITRE Coverage from WSTG */}
                  {(() => {
                    const techniques = {};
                    selectedCampaign.wstgTests.forEach(test => {
                      (test.mitreLinks || []).forEach(link => {
                        if (!techniques[link.techniqueId]) {
                          techniques[link.techniqueId] = { ...link, count: 0 };
                        }
                        techniques[link.techniqueId].count++;
                      });
                    });
                    const techList = Object.values(techniques);
                    if (techList.length === 0) return null;

                    const byTactic = {};
                    techList.forEach(t => {
                      if (!byTactic[t.tactic]) byTactic[t.tactic] = [];
                      byTactic[t.tactic].push(t);
                    });

                    return (
                      <div className={`mt-4 rounded-lg p-4 ${darkMode ? 'bg-red-900/10 border border-red-900/30' : 'bg-red-50 border border-red-200'}`}>
                        <h5 className={`font-medium text-sm mb-2 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                          <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                          MITRE ATT&CK Coverage ({techList.length} techniques)
                        </h5>
                        <div className="flex flex-wrap gap-1">
                          {techList.map(tech => (
                            <span key={tech.techniqueId} className={`text-xs px-1.5 py-0.5 rounded font-mono ${
                              darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'
                            }`}>
                              {tech.techniqueId}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* View Full Matrix button */}
                  <button
                    onClick={() => loadCrossMatrix(selectedCampaign._id || selectedCampaign.id)}
                    className={`mt-3 text-sm px-3 py-1.5 rounded flex items-center gap-2 ${
                      darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    View Full Cross-Framework Matrix
                  </button>
                </div>
              )}

              {/* Cross-links for MITRE campaigns */}
              {selectedCampaign.techniques?.length > 0 && selectedCampaign.framework !== 'owasp_wstg' && (
                <div className="mb-4">
                  <button
                    onClick={() => loadCrossMatrix(selectedCampaign._id || selectedCampaign.id)}
                    className={`text-sm px-3 py-1.5 rounded flex items-center gap-2 ${
                      darkMode ? 'bg-orange-900/20 text-orange-300 hover:bg-orange-900/30 border border-orange-900/30' : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
                    }`}
                  >
                    <FontAwesomeIcon icon={faGlobe} />
                    View Related OWASP WSTG Tests
                  </button>
                </div>
              )}

              {/* Attack Chain (for action-based campaigns) */}
              {getCampaignActions(selectedCampaign).length > 0 && (
                <>
                  <div className="mb-6">
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : ''}`}>Attack Chain</h4>
                    <div className="relative">
                      {getAttackChain(selectedCampaign).map((stage, index) => (
                        <div key={stage.tactic} className="flex items-center mb-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                            stage.success ? 'bg-red-500' : 'bg-green-500'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="ml-4 flex-1">
                            <h5 className={`font-medium ${darkMode ? 'text-white' : ''}`}>{stage.tactic}</h5>
                            <div className="mt-1 space-y-1">
                              {stage.actions.map(action => (
                                <div
                                  key={action._id}
                                  className={`text-sm cursor-pointer rounded px-1 py-0.5 ${
                                    darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-blue-50'
                                  }`}
                                  onClick={() => setSelectedAction(action)}
                                >
                                  • {action.title} ({action.mitreAttackTechnique?.id})
                                  <span className={`ml-2 text-xs ${
                                    action.result === 'successful' ? 'text-red-600' : 'text-green-600'
                                  }`}>
                                    {action.result}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Risk Analysis */}
                  <div className="mb-6">
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : ''}`}>Campaign Risk Analysis</h4>
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-medium ${darkMode ? 'text-gray-200' : ''}`}>Overall Risk Score</span>
                        <span className="text-2xl font-bold text-red-600">
                          {riskScoringService.calculateProjectRisk(getCampaignActions(selectedCampaign), []).score}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div>
                    <h4 className={`font-semibold mb-3 ${darkMode ? 'text-white' : ''}`}>Timeline</h4>
                    <div className="space-y-2">
                      {getCampaignActions(selectedCampaign)
                        .sort((a, b) => new Date(a.startTimestamp) - new Date(b.startTimestamp))
                        .map((action) => (
                          <div
                            key={action._id}
                            className={`flex items-center cursor-pointer rounded transition-colors ${
                              darkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-50'
                            }`}
                            onClick={() => setSelectedAction(action)}
                          >
                            <div className={`w-24 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {format(new Date(action.startTimestamp), 'HH:mm:ss')}
                            </div>
                            <div className={`flex-1 p-3 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                              <div className="flex items-center justify-between">
                                <span className={`font-medium ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                  {action.title}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  action.result === 'successful' ? 'bg-green-100 text-green-800' :
                                  action.result === 'detected' ? 'bg-yellow-100 text-yellow-800' :
                                  action.result === 'blocked' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {action.result || 'unknown'}
                                </span>
                              </div>
                              <div className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {action.mitreAttackTechnique?.id} - {action.mitreAttackTechnique?.name}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={`rounded-lg shadow-md p-6 text-center ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
              <FontAwesomeIcon icon={faShieldAlt} className="text-4xl mb-3 opacity-30" />
              <p>{t('campaigns.selectCampaign') || 'Select a campaign to view details'}</p>
              <p className={`text-sm mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Or click "Create" to build a new campaign using the MITRE ATT&CK tree
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => {
          setSelectedAction(updated);
          setActions(prev => prev.map(a => a._id === updated._id ? updated : a));
        }}
      />

      {/* Templates Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowTemplates(false)}>
          <div className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex justify-between items-center p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`font-semibold text-lg ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                <FontAwesomeIcon icon={faBook} className="mr-2" />
                Campaign Templates
              </h3>
              <button onClick={() => setShowTemplates(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
                <span className="text-lg font-light">&times;</span>
              </button>
            </div>
            <p className={`px-4 pt-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Pre-built campaign scenarios with suggested MITRE ATT&CK techniques
            </p>

            <div className="flex-1 overflow-y-auto p-4">
              {templatesLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : Object.keys(templates).length === 0 ? (
                <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No templates available</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(templates).map(([category, categoryTemplates]) => (
                    <div key={category} className={`border rounded-lg overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <button
                        onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                        className={`w-full flex justify-between items-center p-3 text-left ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                      >
                        <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>
                          {categoryNames[category] || category}
                          <span className={`ml-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>({categoryTemplates.length})</span>
                        </span>
                        <FontAwesomeIcon icon={expandedCategory === category ? faChevronUp : faChevronDown} className={darkMode ? 'text-gray-400' : 'text-gray-400'} />
                      </button>

                      {expandedCategory === category && (
                        <div className={`p-2 space-y-2 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                          {categoryTemplates.map((template) => (
                            <div
                              key={template._id}
                              className={`p-3 rounded border cursor-pointer transition-colors ${
                                selectedTemplate?._id === template._id
                                  ? darkMode ? 'border-purple-500 bg-purple-900/20' : 'border-purple-500 bg-purple-50'
                                  : darkMode ? 'border-gray-700 hover:border-purple-600' : 'border-gray-200 hover:border-purple-300'
                              }`}
                              onClick={() => handleTemplateSelect(template)}
                            >
                              <div className="flex justify-between items-start">
                                <h4 className={`font-medium text-sm ${darkMode ? 'text-white' : ''}`}>{template.name}</h4>
                                <span className={`text-xs px-2 py-0.5 rounded ${difficultyColors[template.difficulty] || 'bg-gray-100'}`}>
                                  {template.difficulty}
                                </span>
                              </div>

                              {selectedTemplate?._id === template._id && (
                                <div className="mt-3 space-y-2 text-sm">
                                  <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{template.description}</p>

                                  <div className={`flex items-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <FontAwesomeIcon icon={faClock} className="mr-1" />
                                    ~{template.estimatedDuration}h
                                    <FontAwesomeIcon icon={faSignal} className="ml-3 mr-1" />
                                    Risk: {template.riskScore}/10
                                  </div>

                                  {template.tags?.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {template.tags.map((tag, idx) => (
                                        <span key={idx} className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100'}`}>
                                          <FontAwesomeIcon icon={faTag} className="mr-1 text-gray-400" />{tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}

                                  {template.suggestedTechniques?.length > 0 && (
                                    <div className="mt-2">
                                      <p className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Suggested Techniques:</p>
                                      <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                                        {template.suggestedTechniques.map((tech, idx) => (
                                          <div key={idx} className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                            • {tech.id} - {tech.name}
                                            <span className={`ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>({tech.tactic})</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <div className="mt-2 flex gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleCreateFromTemplate(template); }}
                                      className="flex-1 bg-purple-600 text-white py-2 px-3 rounded hover:bg-purple-700 flex items-center justify-center text-sm"
                                    >
                                      <FontAwesomeIcon icon={faRocket} className="mr-2" />
                                      Create Campaign
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleCreatePlaybookFromTemplate(template); }}
                                      className="flex-1 bg-green-600 text-white py-2 px-3 rounded hover:bg-green-700 flex items-center justify-center text-sm"
                                    >
                                      <FontAwesomeIcon icon={faPlay} className="mr-2" />
                                      Create Playbook
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cross-Framework Matrix Modal */}
      {showCrossMatrix && crossMatrix && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowCrossMatrix(false)}>
          <div className={`rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex justify-between items-center p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : ''}`}>
                <FontAwesomeIcon icon={faExchangeAlt} className="mr-2 text-purple-500" />
                Cross-Framework Matrix: MITRE ATT&CK / OWASP WSTG
              </h3>
              <button onClick={() => setShowCrossMatrix(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900/20 border border-red-900/30' : 'bg-red-50 border border-red-200'}`}>
                  <div className={`text-sm font-medium mb-1 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                    <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />
                    MITRE ATT&CK Techniques
                  </div>
                  <div className="text-2xl font-bold text-red-500">{crossMatrix.mitreTechniques.length}</div>
                </div>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-orange-900/20 border border-orange-900/30' : 'bg-orange-50 border border-orange-200'}`}>
                  <div className={`text-sm font-medium mb-1 ${darkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                    <FontAwesomeIcon icon={faGlobe} className="mr-1" />
                    OWASP WSTG Tests
                  </div>
                  <div className="text-2xl font-bold text-orange-500">{crossMatrix.wstgTests.length}</div>
                </div>
              </div>

              {crossMatrix.links.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className={darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}>
                      <th className="px-3 py-2 text-left font-medium">MITRE Technique</th>
                      <th className="px-3 py-2 text-left font-medium">WSTG Test</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crossMatrix.links.map((link, i) => (
                      <tr key={i} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <td className="px-3 py-2">
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded mr-1 ${
                            darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                          }`}>{link.mitreTechniqueId}</span>
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{link.mitreTechniqueName}</span>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`font-mono text-xs px-1.5 py-0.5 rounded mr-1 ${
                            darkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-orange-100 text-orange-700'
                          }`}>{link.wstgTestId}</span>
                          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{link.wstgTestName}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No cross-framework links found for this campaign.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legacy Create/Edit Campaign Modal (for action-based campaigns) */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`rounded-lg shadow-xl max-w-2xl w-full p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : ''}`}>
              {editingCampaign ? 'Edit Campaign' : 'Create Custom Campaign'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>Campaign Name *</label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'border-gray-300'}`}
                  placeholder="e.g., Initial Compromise Campaign"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>Objective *</label>
                <textarea
                  value={campaignForm.objective}
                  onChange={(e) => setCampaignForm({...campaignForm, objective: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-md ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'border-gray-300'}`}
                  rows="2"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : ''}`}>Select Actions *</label>
                <div className={`max-h-48 overflow-y-auto border rounded-md p-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  {actions.map(action => (
                    <label key={action._id} className={`flex items-center p-2 cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                      <input
                        type="checkbox"
                        checked={campaignForm.actions.some(a => (a._id || a) === action._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCampaignForm({ ...campaignForm, actions: [...campaignForm.actions, action] });
                          } else {
                            setCampaignForm({ ...campaignForm, actions: campaignForm.actions.filter(a => (a._id || a) !== action._id) });
                          }
                        }}
                        className="mr-2"
                      />
                      <div className="flex-1">
                        <div className={`font-medium ${darkMode ? 'text-white' : ''}`}>{action.title}</div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {format(new Date(action.startTimestamp), 'MMM d, HH:mm')} • {action.result}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); setEditingCampaign(null); }}
                className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCampaign}
                disabled={!campaignForm.name || !campaignForm.objective || campaignForm.actions.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
              >
                {editingCampaign ? 'Update' : 'Create'} Campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignView;
